import { Centrifuge } from 'centrifuge'
import { onBeforeMount, onBeforeUnmount } from 'vue'
import EventEmitter from 'eventemitter3'
import { useAuth } from '@/store/auth'
import { useLocator } from '@/store/locator'
import { getWsToken } from '@/libs/api/auth/getWsToken'
import { deprecateNotification } from '@/libs/functions.js'

/**
 * Event emitter
 * @type {EventEmitter<string | symbol, any>}
 */
const emitter = new EventEmitter()

/** @type {Centrifuge|null} */
let centrifuge = null

/**
 * Centrifuge integration
 *
 * After login websocket connection will be started
 * If user logout and ws_token will be null connection will be finished
 *
 * @param sockets
 * @return {{init: (function(): (function(): Promise<void>)|*), destroy: actions.destroy}}
 */
export const useSockets = (sockets = null) => {
  const auth = useAuth()
  const locator = useLocator()

  /**
   * Prevention equal events
   * @type {{add: eventsGuard.add, getHash: (function(*): *), limit: number, exists: (function(*): boolean), list: *[]}}
   */
  const eventsGuard = {
    limit: 100,
    list: [],
    add: function (hash) {
      this.list.push(hash)

      if (this.list.length > this.limit) {
        let deleteCount = this.list.length - this.limit
        this.list.splice(0, deleteCount)
      }
    },
    exists: function (hash) {
      return this.list.includes(hash)
    },
  }

  const virtualEmitter = {
    emit: (event, params, meta, after) => {
      if (!eventsGuard.exists(meta.id)) {
        eventsGuard.add(meta.id)
        emitter.emit(event, params)

        typeof after === 'function' && after()
      }
    },
  }

  // window.SOCKET = virtualEmitter

  const actions = {
    /**
     * Initialize centrifuge object
     */
    init: () => {
      // watch(auth.ws_token, (newVal, oldValue) => {
      //   if (newVal && oldValue && newVal !== oldValue) {
      //     this.destroy()
      //     this.init()
      //   }
      // })

      if (!centrifuge) {
        centrifuge = new Centrifuge(locator.getWsUrl, {
          // getToken will be called by SDK on initial connect and when server requests a new token
          getToken: async () => {
            const { data } = await getWsToken()
            auth.setWsToken(data)
            return data.token
          },
        })

        centrifuge.on('publication', function ({ data }) {
          const [event, meta, params] =
            data.length === 3 ? data : [data[0], null, data[1]]

          virtualEmitter.emit(event, params, meta, () => {
            deprecateNotification(event, meta.deprecated)
          })
        })

        centrifuge.connect()
      }
    },

    /**
     * Destroy connection object
     */
    destroy: () => {
      if (centrifuge && centrifuge.state === 'connected') {
        centrifuge.disconnect()
      }

      centrifuge = null
    },
  }

  const listeners = {
    add: () => {
      if (sockets && typeof sockets === 'object') {
        for (let event in sockets) {
          if (typeof sockets[event] === 'function') {
            // console.debug('Register: ' + event)
            emitter.on(event, sockets[event])
          }
        }
      }
    },
    remove: () => {
      if (sockets && typeof sockets === 'object') {
        for (let event in sockets) {
          if (typeof sockets[event] === 'function') {
            // console.debug('Unregister: ' + event)
            emitter.off(event, sockets[event])
          }
        }
      }
    },
  }

  if (sockets) {
    onBeforeMount(listeners.add)
    onBeforeUnmount(listeners.remove)
  }

  return {
    c: centrifuge,
    init: actions.init,
    destroy: actions.destroy,
  }
}
